import { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { paywall } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import type { hooksCftSubpageConfigQuery } from "scmp-app/queries/__generated__/hooksCftSubpageConfigQuery.graphql";
import type { hooksCftTopicContentsFragment$key } from "scmp-app/queries/__generated__/hooksCftTopicContentsFragment.graphql";
import type { hooksCftTopicContentsQuery } from "scmp-app/queries/__generated__/hooksCftTopicContentsQuery.graphql";

import type {
  CftSubpageConfigItemNodequeue,
  CftSubpageConfigItemSeries,
  CftSubpageConfigJson,
} from "./type";

export const useCftSubpageConfig = () => {
  const queryReference = useLazyLoadQuery<hooksCftSubpageConfigQuery>(
    graphql`
      query hooksCftSubpageConfigQuery {
        appConfig(filter: { entityId: "scmp_plus_cft_subpage" }) {
          json
        }
      }
    `,
    {},
    {
      fetchPolicy: "store-or-network",
    },
  );

  return useMemo(() => {
    const industries = (queryReference.appConfig?.json as CftSubpageConfigJson)?.industries ?? [];
    return {
      nodequeueItems: industries.filter(
        item => item.type === "nodequeue" && notEmpty(item.name) && notEmpty(item.queueName),
      ) as CftSubpageConfigItemNodequeue[],
      seriesItems: industries.filter(
        item => item.type === "series" && notEmpty(item.name) && notEmpty(item.seriesEntityId),
      ) as CftSubpageConfigItemSeries[],
    };
  }, [queryReference]);
};

export const useCftContentList = (topicIds: string[]) => {
  const pageSize = 30;

  const queryReference = useLazyLoadQuery<hooksCftTopicContentsQuery>(
    graphql`
      query hooksCftTopicContentsQuery(
        $after: String
        $pageSize: Int!
        $topicIds: [String!]!
        $paywallTypeIds: [String!]!
      ) {
        ...hooksCftTopicContentsFragment
          @arguments(
            after: $after
            count: $pageSize
            topicIds: $topicIds
            paywallTypeIds: $paywallTypeIds
          )
      }
    `,
    {
      pageSize,
      paywallTypeIds: [paywall.scmpPlus.entityUuid],
      topicIds: topicIds.length > 0 ? topicIds : ["_"],
    },
    {
      fetchPolicy: "store-or-network",
    },
  );

  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
    hooksCftTopicContentsQuery,
    hooksCftTopicContentsFragment$key
  >(
    graphql`
      fragment hooksCftTopicContentsFragment on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int!" }
        topicIds: { type: "[String!]!" }
        paywallTypeIds: { type: "[String!]!" }
      )
      @refetchable(queryName: "hooksCftTopicContentsPaginationQuery") {
        contents(
          after: $after
          contentTypes: [ARTICLE]
          articleFilterInput: { paywallTypeIds: $paywallTypeIds }
          filter: { topicIds: $topicIds }
          first: $count
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "hooksCftTopicContentsFragment_contents") {
          edges {
            node {
              entityId
              publishedDate
              ...hooksPlusArticleLinkHandlerArticle
              ...plusCftContentItemContent
            }
          }
        }
      }
    `,
    queryReference,
  );

  const contents = useMemo(() => {
    const unique = new Set<string>();
    return (data?.contents?.edges ?? [])
      .map(({ node }) => {
        if (unique.has(node.entityId)) return;
        unique.add(node.entityId);
        return node;
      })
      .filter(node => notEmpty(node))
      .toSorted((a, b) => (b.publishedDate ?? 0) - (a.publishedDate ?? 0));
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (!hasNext || isLoadingNext) return;
    loadNext(pageSize);
  }, [hasNext, isLoadingNext, loadNext, pageSize]);

  return {
    contents,
    hasNext,
    isLoading: isLoadingNext,
    loadMore: handleLoadMore,
  };
};
